import React, { useCallback, useEffect, useMemo, useState } from "react";
import { Plus, Pencil, Trash2, X, Search, Download, Copy, FileSpreadsheet, FileText, PackageCheck, Upload, FileDown, AlertCircle } from "lucide-react";
import { Input } from "./ui/input";
import { Button } from "./ui/button";
import { API_BASE } from "../DataContext";
import { GRNModal } from "./GRNModal";

const STATUSES = ["Draft", "Approved", "Sent", "Closed", "Cancelled"];
const STATUS_CLR = {
  Draft: "bg-slate-100 text-slate-700",
  Approved: "bg-emerald-100 text-emerald-700",
  Sent: "bg-sky-100 text-sky-700",
  Closed: "bg-violet-100 text-violet-700",
  Cancelled: "bg-red-100 text-red-700",
};

const QtyList = ({ title, rows, kind, testid }) => {
  const qtyKey = kind === "excess" ? "excess_qty" : "pending_qty";
  const color = kind === "excess" ? "text-red-600" : "text-amber-600";
  return (
    <div data-testid={testid}>
      <div className="text-xs font-bold uppercase tracking-wide text-slate-500 mb-2">{title}</div>
      {rows.length === 0 ? (
        <div className="text-xs text-slate-400 italic bg-slate-50 rounded-lg p-3">No lines.</div>
      ) : (
        <div className="max-h-72 overflow-y-auto border border-slate-200 rounded-lg divide-y divide-slate-100">
          {rows.map((r, i) => (
            <div key={`${r.po_id}-${r.item_index}-${i}`} className="px-3 py-2 flex items-center justify-between gap-3 text-xs hover:bg-slate-50">
              <div className="min-w-0">
                <div className="font-semibold text-slate-800 truncate">{r.item}</div>
                <div className="text-slate-400 truncate">
                  <span className="font-mono">{r.po_number}</span> · {r.vendor_name || "—"}
                </div>
              </div>
              <div className={`shrink-0 font-bold ${color} whitespace-nowrap`}>
                {kind === "excess" ? "+" : ""}{Number(r[qtyKey]).toLocaleString("en-IN", { maximumFractionDigits: 3 })} {r.unit}
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

const inr = (n) => Number(n || 0).toLocaleString("en-IN", { minimumFractionDigits: 2, maximumFractionDigits: 2 });
const fmtDate = (iso) => {
  if (!iso) return "—";
  const [y, m, d] = String(iso).split("-");
  return y && m && d ? `${d}-${m}-${y}` : iso;
};

export function AdminPurchaseOrders({ token, onUnauthorized }) {
  const [pos, setPOs] = useState(null);
  const [vendors, setVendors] = useState([]);
  const [items, setItems] = useState([]);
  const [q, setQ] = useState("");
  const [statusFilter, setStatusFilter] = useState("");
  const [editing, setEditing] = useState(null); // null | "new" | po object
  const [grnFor, setGrnFor] = useState(null); // po object for GRN modal
  const [excessReport, setExcessReport] = useState(null);
  const [showExcessPanel, setShowExcessPanel] = useState(false);

  const authHeader = { Authorization: `Bearer ${token}` };

  const load = useCallback(async () => {
    const [pr, vr, ir, er] = await Promise.all([
      fetch(`${API_BASE}/api/admin/purchase-orders`, { headers: authHeader }),
      fetch(`${API_BASE}/api/admin/vendors`, { headers: authHeader }),
      fetch(`${API_BASE}/api/admin/items`, { headers: authHeader }),
      fetch(`${API_BASE}/api/admin/purchase-orders/excess-report`, { headers: authHeader }),
    ]);
    if (pr.status === 401 || vr.status === 401) return onUnauthorized();
    setPOs(await pr.json());
    setVendors(await vr.json());
    setItems(ir.ok ? await ir.json() : []);
    setExcessReport(er.ok ? await er.json() : null);
  }, [token, onUnauthorized]);

  useEffect(() => { load(); }, [load]);

  const downloadPdf = async (po) => {
    const r = await fetch(`${API_BASE}/api/admin/purchase-orders/${po.id}/pdf`, { headers: authHeader });
    if (!r.ok) return alert("PDF generation failed");
    const blob = await r.blob();
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = po.po_number.replace(/\//g, "_") + ".pdf";
    document.body.appendChild(a); a.click(); a.remove();
    URL.revokeObjectURL(url);
  };

  const duplicate = async (po) => {
    const r = await fetch(`${API_BASE}/api/admin/purchase-orders/${po.id}/duplicate`, { method: "POST", headers: authHeader });
    if (!r.ok) return alert("Duplicate failed");
    await load();
  };

  const remove = async (po) => {
    if (!confirm(`Delete PO ${po.po_number}? This cannot be undone.`)) return;
    const r = await fetch(`${API_BASE}/api/admin/purchase-orders/${po.id}`, { method: "DELETE", headers: authHeader });
    if (r.ok) load();
  };

  const exportExcel = async () => {
    const r = await fetch(`${API_BASE}/api/admin/purchase-orders/export/excel`, { headers: authHeader });
    if (!r.ok) return alert("Export failed");
    const blob = await r.blob();
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url; a.download = "purchase_orders.xlsx";
    document.body.appendChild(a); a.click(); a.remove();
    URL.revokeObjectURL(url);
  };

  const [showExcessOnly, setShowExcessOnly] = useState(false);

  const filtered = useMemo(() => (pos || []).filter((p) => {
    if (statusFilter && p.status !== statusFilter) return false;
    if (showExcessOnly && !p.summary?.has_excess) return false;
    if (!q) return true;
    const s = q.toLowerCase();
    return (p.po_number + " " + (p.vendor_snapshot?.name || "") + " " + (p.reference_no || "")).toLowerCase().includes(s);
  }), [pos, q, statusFilter, showExcessOnly]);

  const excessCount = useMemo(() => (pos || []).filter((p) => p.summary?.has_excess).length, [pos]);

  if (editing !== null) {
    return (
      <POForm
        initial={editing === "new" ? null : editing}
        vendors={vendors}
        itemsMaster={items}
        token={token}
        onCancel={() => setEditing(null)}
        onSaved={() => { setEditing(null); load(); }}
      />
    );
  }

  return (
    <div data-testid="admin-purchase-orders">
      {excessReport && (excessReport.totals.excess_qty > 0 || excessReport.totals.pending_qty > 0) && (
        <div className="mb-4 rounded-2xl border border-slate-200 bg-white overflow-hidden" data-testid="qty-summary-panel">
          <button onClick={() => setShowExcessPanel((v) => !v)} className="w-full flex items-center justify-between px-4 py-3 hover:bg-slate-50 transition text-left">
            <div className="flex items-center gap-3">
              <span className="inline-flex items-center justify-center h-8 w-8 rounded-full bg-red-100 text-red-700"><AlertCircle className="h-4 w-4" /></span>
              <div>
                <div className="text-sm font-bold text-[#0a2a5e]">PO Quantity Overview — Excess & Pending</div>
                <div className="text-xs text-slate-500">
                  Ordered {inr(excessReport.totals.ordered_qty)} · Invoiced {inr(excessReport.totals.invoiced_qty)} ·
                  <span className="text-red-600 font-semibold"> Excess +{inr(excessReport.totals.excess_qty)}</span> ·
                  <span className="text-amber-600 font-semibold"> Pending {inr(excessReport.totals.pending_qty)}</span>
                </div>
              </div>
            </div>
            <span className="text-xs text-slate-400">{showExcessPanel ? "Hide details ▲" : "View details ▼"}</span>
          </button>
          {showExcessPanel && (
            <div className="border-t border-slate-100 p-4 grid gap-4 md:grid-cols-2">
              <QtyList title={`Excess (${excessReport.excess_lines.length})`} rows={excessReport.excess_lines} kind="excess" testid="excess-lines-list" />
              <QtyList title={`Pending (${excessReport.pending_lines.length})`} rows={excessReport.pending_lines} kind="pending" testid="pending-lines-list" />
            </div>
          )}
        </div>
      )}

      <div className="flex flex-wrap items-center gap-3 mb-4">
        <div className="relative flex-1 min-w-[240px]">
          <Search className="h-4 w-4 absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
          <Input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search PO no, vendor, ref…" className="pl-9" data-testid="po-search" />
        </div>
        <select value={statusFilter} onChange={(e) => setStatusFilter(e.target.value)} data-testid="po-status-filter" className="h-10 rounded-md border border-slate-200 px-3 text-sm bg-white">
          <option value="">All statuses</option>
          {STATUSES.map((s) => <option key={s}>{s}</option>)}
        </select>
        {excessCount > 0 && (
          <button
            onClick={() => setShowExcessOnly((v) => !v)}
            data-testid="po-excess-filter"
            className={`h-10 rounded-full px-4 text-xs font-bold flex items-center gap-1.5 border transition ${showExcessOnly ? "bg-red-500 text-white border-red-500" : "bg-red-50 text-red-700 border-red-200 hover:bg-red-100"}`}
            title="Show only POs where vendor invoiced more than ordered qty"
          >
            <AlertCircle className="h-3.5 w-3.5" /> Excess ({excessCount})
          </button>
        )}
        <Button variant="outline" onClick={exportExcel} data-testid="po-export-btn" className="rounded-full">
          <FileSpreadsheet className="h-4 w-4 mr-1" /> Export Excel
        </Button>
        <Button onClick={() => setEditing("new")} data-testid="po-new-btn" className="bg-[#0a2a5e] hover:bg-[#0a2a5e]/90 text-white rounded-full">
          <Plus className="h-4 w-4 mr-1" /> New Purchase Order
        </Button>
      </div>

      <div className="bg-white rounded-2xl border border-slate-100 shadow-sm overflow-x-auto" data-testid="po-table">
        <table className="w-full text-sm">
          <thead>
            <tr className="bg-slate-50 text-left text-xs uppercase tracking-wide text-slate-500">
              <th className="px-4 py-3 font-semibold">PO Number</th>
              <th className="px-4 py-3 font-semibold">Date</th>
              <th className="px-4 py-3 font-semibold">Vendor</th>
              <th className="px-4 py-3 font-semibold text-right">Grand Total</th>
              <th className="px-4 py-3 font-semibold">Status</th>
              <th className="px-4 py-3 font-semibold text-right">Actions</th>
            </tr>
          </thead>
          <tbody>
            {!pos && <tr><td colSpan={6} className="px-4 py-8 text-center text-slate-400">Loading…</td></tr>}
            {pos && filtered.length === 0 && <tr><td colSpan={6} className="px-4 py-8 text-center text-slate-400">No purchase orders yet.</td></tr>}
            {filtered.map((p) => (
              <tr key={p.id} className="border-t border-slate-100 hover:bg-sky-50/50">
                <td className="px-4 py-3 font-mono text-xs font-semibold text-[#0a2a5e]">
                  <div className="flex items-center gap-1.5">
                    {p.po_number}
                    {p.summary?.has_excess && (
                      <span data-testid={`po-excess-badge-${p.id}`} title={`Excess: +${inr(p.summary.excess_qty)} qty`} className="inline-flex items-center gap-0.5 rounded-full bg-red-100 text-red-700 text-[10px] font-bold px-1.5 py-0.5">
                        <AlertCircle className="h-2.5 w-2.5" /> EXCESS
                      </span>
                    )}
                  </div>
                </td>
                <td className="px-4 py-3 text-slate-600 whitespace-nowrap">{fmtDate(p.po_date)}</td>
                <td className="px-4 py-3 font-semibold text-slate-800">{p.vendor_snapshot?.name || "—"}</td>
                <td className="px-4 py-3 text-right font-semibold whitespace-nowrap">₹ {inr(p.totals?.grand_total)}</td>
                <td className="px-4 py-3">
                  <span className={`inline-block px-2.5 py-1 rounded-full text-xs font-semibold ${STATUS_CLR[p.status] || STATUS_CLR.Draft}`}>{p.status}</span>
                </td>
                <td className="px-4 py-3 text-right whitespace-nowrap">
                  <button onClick={() => setGrnFor(p)} data-testid={`po-grn-${p.id}`} title="Record GRN" className="text-amber-600 hover:text-amber-800 p-1.5"><PackageCheck className="h-4 w-4" /></button>
                  <button onClick={() => downloadPdf(p)} data-testid={`po-pdf-${p.id}`} title="Download PDF" className="text-emerald-600 hover:text-emerald-800 p-1.5"><Download className="h-4 w-4" /></button>
                  <button onClick={() => duplicate(p)} data-testid={`po-dup-${p.id}`} title="Duplicate" className="text-violet-600 hover:text-violet-800 p-1.5"><Copy className="h-4 w-4" /></button>
                  <button onClick={() => setEditing(p)} data-testid={`po-edit-${p.id}`} title="Edit" className="text-sky-600 hover:text-sky-800 p-1.5"><Pencil className="h-4 w-4" /></button>
                  <button onClick={() => remove(p)} data-testid={`po-del-${p.id}`} title="Delete" className="text-red-500 hover:text-red-700 p-1.5"><Trash2 className="h-4 w-4" /></button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {grnFor && <GRNModal token={token} po={grnFor} onClose={() => setGrnFor(null)} onSaved={load} />}
    </div>
  );
}

// -------------------- PO Form --------------------
const NEW_ITEM = { item: "", description: "", hsn: "", unit: "Pcs", qty: 1, rate: 0, discount_pct: 0, gst_pct: 18 };

const emptyPO = () => ({
  po_date: new Date().toISOString().slice(0, 10),
  po_number: "",
  vendor_id: "",
  vendor_snapshot: {},
  bill_to: {},
  ship_to: {},
  reference_no: "",
  reference_date: "",
  payment_terms: "",
  delivery_terms: "",
  expected_delivery: "",
  items: [{ ...NEW_ITEM }],
  freight: 0,
  remarks: "",
  status: "Draft",
});

function POForm({ initial, vendors, itemsMaster = [], token, onCancel, onSaved }) {
  const [form, setForm] = useState(() => initial ? { ...initial } : emptyPO());
  const [saving, setSaving] = useState(false);
  const isEdit = !!initial;

  const setField = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const pickVendor = (id) => {
    const v = vendors.find((x) => x.id === id);
    setForm((f) => ({
      ...f,
      vendor_id: id,
      vendor_snapshot: v ? { name: v.name, gstin: v.gstin, pan: v.pan, address: v.address, city: v.city, state: v.state, pincode: v.pincode, contact_person: v.contact_person, email: v.email, mobile: v.mobile } : {},
    }));
  };

  const updateItem = (idx, patch) => setForm((f) => ({ ...f, items: f.items.map((it, i) => (i === idx ? { ...it, ...patch } : it)) }));
  const addItem = () => setForm((f) => ({ ...f, items: [...f.items, { ...NEW_ITEM }] }));
  const removeItem = (idx) => setForm((f) => ({ ...f, items: f.items.filter((_, i) => i !== idx) }));

  // Autofill row from Item Master on selection
  const pickMasterItem = (idx, name) => {
    const m = itemsMaster.find((x) => x.name === name);
    if (!m) { updateItem(idx, { item: name }); return; }
    updateItem(idx, {
      item: m.name,
      description: m.description || "",
      hsn: m.hsn || "",
      unit: m.unit || "Pcs",
      gst_pct: Number(m.gst_pct) || 0,
      rate: Number(m.default_rate) || 0,
    });
  };

  // Live totals
  const totals = useMemo(() => {
    let subtotal = 0, discount = 0, taxable = 0, gst = 0;
    (form.items || []).forEach((it) => {
      const qty = Number(it.qty) || 0, rate = Number(it.rate) || 0, d = Number(it.discount_pct) || 0, g = Number(it.gst_pct) || 0;
      const gross = qty * rate;
      const disc = gross * d / 100;
      const tax = gross - disc;
      subtotal += gross; discount += disc; taxable += tax; gst += tax * g / 100;
    });
    const freight = Number(form.freight) || 0;
    const total = taxable + freight + gst;
    const grand = Math.round(total);
    const roundOff = grand - total;
    const home = "west bengal";
    const vs = (form.vendor_snapshot?.state || "").trim().toLowerCase();
    const interstate = vs && vs !== home;
    return { subtotal, discount, taxable: taxable + freight, gst, cgst: interstate ? 0 : gst / 2, sgst: interstate ? 0 : gst / 2, igst: interstate ? gst : 0, freight, roundOff, grand, interstate };
  }, [form.items, form.freight, form.vendor_snapshot]);

  const save = async (status) => {
    if (!form.vendor_id) return alert("Please select a vendor");
    if (!form.items.length || !form.items[0].item.trim()) return alert("Please add at least one item");
    setSaving(true);
    try {
      const payload = { ...form, status: status || form.status };
      // Coerce numbers
      payload.freight = Number(payload.freight) || 0;
      payload.items = payload.items.map((it) => ({
        ...it,
        qty: Number(it.qty) || 0,
        rate: Number(it.rate) || 0,
        discount_pct: Number(it.discount_pct) || 0,
        gst_pct: Number(it.gst_pct) || 0,
      }));
      const url = isEdit ? `${API_BASE}/api/admin/purchase-orders/${initial.id}` : `${API_BASE}/api/admin/purchase-orders`;
      const method = isEdit ? "PUT" : "POST";
      const r = await fetch(url, { method, headers: { Authorization: `Bearer ${token}`, "Content-Type": "application/json" }, body: JSON.stringify(payload) });
      if (!r.ok) throw new Error((await r.json()).detail || "Save failed");
      const saved = await r.json();
      onSaved(saved);
    } catch (e) { alert(e.message); }
    finally { setSaving(false); }
  };

  return (
    <div className="bg-white rounded-2xl border border-slate-100 shadow-sm p-6 space-y-6" data-testid="po-form">
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-2xl font-black text-[#0a2a5e]">{isEdit ? `Edit ${initial.po_number}` : "New Purchase Order"}</h2>
          <p className="text-sm text-slate-500">Leave PO number blank to auto-generate (format: JJTPL/PO/YY-YY/NNNN)</p>
        </div>
        <button onClick={onCancel} className="text-slate-400 hover:text-slate-600 p-2" data-testid="po-form-close"><X className="h-5 w-5" /></button>
      </div>

      {/* Header meta */}
      <div className="grid md:grid-cols-4 gap-3">
        <Field label={isEdit ? "PO Number *" : "PO Number (blank = auto)"}>
          <Input value={form.po_number} onChange={(e) => setField("po_number", e.target.value)} data-testid="po-number-input" placeholder="e.g. JJTPL/PO/26-27/0023" className="font-mono" />
        </Field>
        <Field label="PO Date *"><Input type="date" value={form.po_date} onChange={(e) => setField("po_date", e.target.value)} data-testid="po-date" /></Field>
        <Field label="Vendor *">
          <select value={form.vendor_id} onChange={(e) => pickVendor(e.target.value)} data-testid="po-vendor" className="h-10 w-full rounded-md border border-slate-200 px-3 text-sm bg-white">
            <option value="">— Select vendor —</option>
            {vendors.map((v) => <option key={v.id} value={v.id}>{v.name} {v.state ? `(${v.state})` : ""}</option>)}
          </select>
        </Field>
        <Field label="Reference No"><Input value={form.reference_no} onChange={(e) => setField("reference_no", e.target.value)} /></Field>
        <Field label="Reference Date"><Input type="date" value={form.reference_date} onChange={(e) => setField("reference_date", e.target.value)} /></Field>
        <Field label="Payment Terms"><Input value={form.payment_terms} onChange={(e) => setField("payment_terms", e.target.value)} placeholder="e.g. 30 days credit" /></Field>
        <Field label="Delivery Terms"><Input value={form.delivery_terms} onChange={(e) => setField("delivery_terms", e.target.value)} placeholder="e.g. Ex-Works / FOR" /></Field>
        <Field label="Expected Delivery"><Input type="date" value={form.expected_delivery} onChange={(e) => setField("expected_delivery", e.target.value)} /></Field>
        <Field label="Status">
          <select value={form.status} onChange={(e) => setField("status", e.target.value)} className="h-10 w-full rounded-md border border-slate-200 px-3 text-sm bg-white">
            {STATUSES.map((s) => <option key={s}>{s}</option>)}
          </select>
        </Field>
      </div>

      {/* Vendor snapshot (readonly info) */}
      {form.vendor_snapshot?.name && (
        <div className="grid md:grid-cols-2 gap-4 bg-slate-50 rounded-xl p-4 text-sm">
          <div>
            <div className="text-xs font-semibold text-slate-500 mb-1">Vendor Details</div>
            <div className="font-semibold text-[#0a2a5e]">{form.vendor_snapshot.name}</div>
            <div className="text-slate-600">{form.vendor_snapshot.address}</div>
            <div className="text-slate-600">{[form.vendor_snapshot.city, form.vendor_snapshot.pincode].filter(Boolean).join(" ")} {form.vendor_snapshot.state}</div>
            {form.vendor_snapshot.gstin && <div className="text-slate-500 text-xs mt-1">GSTIN: {form.vendor_snapshot.gstin}</div>}
          </div>
          <div>
            <div className="text-xs font-semibold text-slate-500 mb-1">GST Type</div>
            <div className="text-slate-700">
              {totals.interstate ? (
                <span>Inter-state → <b className="text-[#0a2a5e]">IGST</b> will apply</span>
              ) : (
                <span>Intra-state (West Bengal) → <b className="text-[#0a2a5e]">CGST + SGST</b></span>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Item table */}
      <div>
        <div className="flex flex-wrap items-center justify-between gap-2 mb-2">
          <h3 className="font-semibold text-[#0a2a5e]">Items</h3>
          <div className="flex flex-wrap items-center gap-2">
            <BulkImportControls token={token} form={form} setForm={setForm} />
            <Button type="button" size="sm" variant="outline" onClick={addItem} data-testid="po-add-item" className="rounded-full">
              <Plus className="h-4 w-4 mr-1" /> Add Row
            </Button>
          </div>
        </div>
        <div className="overflow-x-auto border border-slate-100 rounded-xl">
          <table className="w-full text-sm min-w-[900px]">
            <thead>
              <tr className="bg-slate-50 text-xs text-slate-500 uppercase">
                <th className="px-2 py-2 text-left w-10">#</th>
                <th className="px-2 py-2 text-left">Item</th>
                <th className="px-2 py-2 text-left">Description</th>
                <th className="px-2 py-2 text-left w-24">HSN</th>
                <th className="px-2 py-2 text-left w-20">Unit</th>
                <th className="px-2 py-2 text-right w-20">Qty</th>
                <th className="px-2 py-2 text-right w-24">Rate</th>
                <th className="px-2 py-2 text-right w-20">Disc%</th>
                <th className="px-2 py-2 text-right w-20">GST%</th>
                <th className="px-2 py-2 text-right w-28">Taxable</th>
                <th className="w-10"></th>
              </tr>
            </thead>
            <tbody>
              {form.items.map((it, idx) => {
                const gross = (Number(it.qty) || 0) * (Number(it.rate) || 0);
                const tax = gross - gross * (Number(it.discount_pct) || 0) / 100;
                return (
                  <tr key={idx} className="border-t border-slate-100">
                    <td className="px-2 py-1 text-slate-400 text-center">{idx + 1}</td>
                    <td className="px-1 py-1"><input value={it.item} onChange={(e) => pickMasterItem(idx, e.target.value)} data-testid={`po-item-name-${idx}`} list={`items-master-${idx}`} placeholder="Type or pick" className="w-full px-2 py-1 rounded border border-slate-200 text-sm" />
                      <datalist id={`items-master-${idx}`}>
                        {itemsMaster.map((m) => <option key={m.id} value={m.name}>{m.hsn ? `${m.hsn} · ` : ""}{m.description || m.unit}</option>)}
                      </datalist>
                    </td>
                    <td className="px-1 py-1"><input value={it.description} onChange={(e) => updateItem(idx, { description: e.target.value })} className="w-full px-2 py-1 rounded border border-slate-200 text-sm" /></td>
                    <td className="px-1 py-1"><input value={it.hsn} onChange={(e) => updateItem(idx, { hsn: e.target.value })} className="w-full px-2 py-1 rounded border border-slate-200 text-sm" /></td>
                    <td className="px-1 py-1"><input value={it.unit} onChange={(e) => updateItem(idx, { unit: e.target.value })} className="w-full px-2 py-1 rounded border border-slate-200 text-sm" /></td>
                    <td className="px-1 py-1"><input type="number" step="any" value={it.qty} onChange={(e) => updateItem(idx, { qty: e.target.value })} data-testid={`po-item-qty-${idx}`} className="w-full px-2 py-1 rounded border border-slate-200 text-sm text-right" /></td>
                    <td className="px-1 py-1"><input type="number" step="any" value={it.rate} onChange={(e) => updateItem(idx, { rate: e.target.value })} data-testid={`po-item-rate-${idx}`} className="w-full px-2 py-1 rounded border border-slate-200 text-sm text-right" /></td>
                    <td className="px-1 py-1"><input type="number" step="any" value={it.discount_pct} onChange={(e) => updateItem(idx, { discount_pct: e.target.value })} className="w-full px-2 py-1 rounded border border-slate-200 text-sm text-right" /></td>
                    <td className="px-1 py-1"><input type="number" step="any" value={it.gst_pct} onChange={(e) => updateItem(idx, { gst_pct: e.target.value })} className="w-full px-2 py-1 rounded border border-slate-200 text-sm text-right" /></td>
                    <td className="px-2 py-1 text-right font-semibold text-slate-700">₹ {inr(tax)}</td>
                    <td className="px-1 py-1 text-center">
                      {form.items.length > 1 && <button type="button" onClick={() => removeItem(idx)} data-testid={`po-item-remove-${idx}`} className="text-red-500 hover:text-red-700"><Trash2 className="h-4 w-4" /></button>}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </div>

      {/* Totals + Remarks */}
      <div className="grid md:grid-cols-2 gap-4">
        <div>
          <Field label="Remarks">
            <textarea value={form.remarks} onChange={(e) => setField("remarks", e.target.value)} rows={5} data-testid="po-remarks" className="w-full rounded-md border border-slate-200 px-3 py-2 text-sm" placeholder="Delivery instructions, order acceptance note…" />
          </Field>
          <Field label="Freight / P&F Charges (₹)">
            <Input type="number" step="any" value={form.freight} onChange={(e) => setField("freight", e.target.value)} data-testid="po-freight" />
          </Field>
        </div>
        <div className="bg-slate-50 rounded-xl p-4 text-sm space-y-1.5">
          <Row label="Sub Total" value={inr(totals.subtotal)} />
          <Row label="Discount" value={`(${inr(totals.discount)})`} />
          <Row label="Freight / P&F" value={inr(totals.freight)} />
          <Row label="Taxable Amount" value={inr(totals.taxable)} bold />
          {totals.interstate
            ? <Row label="IGST" value={inr(totals.igst)} />
            : <><Row label="CGST" value={inr(totals.cgst)} /><Row label="SGST" value={inr(totals.sgst)} /></>}
          {Math.abs(totals.roundOff) >= 0.01 && <Row label="Round Off" value={totals.roundOff.toFixed(2)} />}
          <div className="border-t-2 border-[#0a2a5e] pt-2 mt-2">
            <Row label="Grand Total (INR)" value={`₹ ${inr(totals.grand)}`} big />
          </div>
        </div>
      </div>

      <div className="flex flex-wrap justify-end gap-2 pt-4 border-t">
        <Button type="button" variant="outline" onClick={onCancel} className="rounded-full">Cancel</Button>
        <Button type="button" onClick={() => save("Draft")} disabled={saving} data-testid="po-save-draft" variant="outline" className="rounded-full">
          {saving ? "Saving…" : "Save as Draft"}
        </Button>
        <Button type="button" onClick={() => save(form.status === "Draft" ? "Approved" : form.status)} disabled={saving} data-testid="po-save-approve" className="bg-[#0a2a5e] hover:bg-[#0a2a5e]/90 text-white rounded-full">
          <FileText className="h-4 w-4 mr-1" /> {saving ? "Saving…" : (isEdit ? "Save Changes" : "Save & Approve")}
        </Button>
      </div>
    </div>
  );
}

const Field = ({ label, children }) => (
  <div>
    <label className="block text-xs font-semibold text-slate-500 mb-1">{label}</label>
    {children}
  </div>
);

const Row = ({ label, value, bold, big }) => (
  <div className={`flex justify-between ${bold ? "font-semibold text-[#0a2a5e]" : "text-slate-600"} ${big ? "text-lg" : ""}`}>
    <span>{label}</span>
    <span>{value}</span>
  </div>
);

// ============ Bulk Import / Export controls for PO items ============
function BulkImportControls({ token, form, setForm }) {
  const [busy, setBusy] = useState("");
  const [result, setResult] = useState(null); // { summary, errors, items } from server
  const [addToMaster, setAddToMaster] = useState(false);
  const fileInputRef = React.useRef();
  const authHeader = { Authorization: `Bearer ${token}` };

  const downloadTemplate = async () => {
    setBusy("template");
    try {
      const r = await fetch(`${API_BASE}/api/admin/purchase-orders/items/template`, { headers: authHeader });
      if (!r.ok) throw new Error("Template download failed");
      const blob = await r.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url; a.download = "po_items_template.xlsx";
      document.body.appendChild(a); a.click(); a.remove();
      URL.revokeObjectURL(url);
    } catch (e) { alert(e.message); }
    finally { setBusy(""); }
  };

  const exportCurrent = async () => {
    if (!form.items || !form.items.length) return alert("No items to export yet.");
    setBusy("export");
    try {
      const r = await fetch(`${API_BASE}/api/admin/purchase-orders/items/export`, {
        method: "POST",
        headers: { ...authHeader, "Content-Type": "application/json" },
        body: JSON.stringify({ items: form.items }),
      });
      if (!r.ok) throw new Error("Export failed");
      const blob = await r.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url; a.download = "po_items_export.xlsx";
      document.body.appendChild(a); a.click(); a.remove();
      URL.revokeObjectURL(url);
    } catch (e) { alert(e.message); }
    finally { setBusy(""); }
  };

  const onFilePicked = async (e) => {
    const file = e.target.files?.[0];
    e.target.value = ""; // reset so same file can be re-picked
    if (!file) return;
    setBusy("import"); setResult(null);
    try {
      const fd = new FormData(); fd.append("file", file);
      const r = await fetch(`${API_BASE}/api/admin/purchase-orders/items/import`, {
        method: "POST", headers: authHeader, body: fd,
      });
      const data = await r.json().catch(() => ({}));
      if (!r.ok) throw new Error(data.detail || "Import failed");
      setResult(data);
    } catch (err) {
      alert(err.message || "Import failed");
    } finally { setBusy(""); }
  };

  const applyImported = async (mode) => {
    if (!result?.items?.length) return;
    // If the user opted-in and there are new items, push them to the Item Master first.
    if (addToMaster && (result.summary?.missing_from_master || 0) > 0) {
      try {
        setBusy("master");
        const r = await fetch(`${API_BASE}/api/admin/purchase-orders/items/bulk-add-to-master`, {
          method: "POST",
          headers: { ...authHeader, "Content-Type": "application/json" },
          body: JSON.stringify({ items: result.items.filter((it) => !it.in_master && it.item) }),
        });
        const data = await r.json().catch(() => ({}));
        if (!r.ok) throw new Error(data.detail || "Failed to add items to master");
        if (data.added > 0) alert(`Added ${data.added} new item${data.added === 1 ? "" : "s"} to the Item Master.`);
      } catch (err) {
        if (!confirm(`Warning: ${err.message}\n\nContinue adding these items to the PO anyway?`)) {
          setBusy("");
          return;
        }
      } finally {
        setBusy("");
      }
    }
    setForm((f) => {
      const hasContent = (it) => (it.item && it.item.trim()) || (it.description && it.description.trim()) || Number(it.qty) > 0;
      const existing = (f.items || []).filter(hasContent);
      // Strip the frontend-only in_master flag before applying
      const clean = result.items.map(({ in_master, ...rest }) => rest);
      const next = mode === "replace" ? [...clean] : [...existing, ...clean];
      return { ...f, items: next.length ? next : f.items };
    });
    setResult(null);
    setAddToMaster(false);
  };

  return (
    <>
      <div className="flex flex-wrap items-center gap-2" data-testid="po-bulk-controls">
        <Button type="button" size="sm" variant="outline" onClick={downloadTemplate} disabled={busy === "template"} data-testid="po-bulk-template" className="rounded-full">
          <FileDown className="h-4 w-4 mr-1" /> {busy === "template" ? "…" : "Template"}
        </Button>
        <Button type="button" size="sm" variant="outline" onClick={exportCurrent} disabled={busy === "export"} data-testid="po-bulk-export" className="rounded-full">
          <FileSpreadsheet className="h-4 w-4 mr-1" /> {busy === "export" ? "…" : "Export"}
        </Button>
        <Button type="button" size="sm" variant="outline" onClick={() => fileInputRef.current?.click()} disabled={busy === "import"} data-testid="po-bulk-import" className="rounded-full">
          <Upload className="h-4 w-4 mr-1" /> {busy === "import" ? "Uploading…" : "Import"}
        </Button>
        <input ref={fileInputRef} type="file" accept=".xlsx,.xls,.csv" onChange={onFilePicked} className="hidden" data-testid="po-bulk-file" />
      </div>

      {result && (
        <ImportResultModal
          result={result}
          onClose={() => { setResult(null); setAddToMaster(false); }}
          onApply={applyImported}
          addToMaster={addToMaster}
          setAddToMaster={setAddToMaster}
          applyBusy={busy === "master"}
        />
      )}
    </>
  );
}

function ImportResultModal({ result, onClose, onApply, addToMaster, setAddToMaster, applyBusy }) {
  const { summary, items, errors } = result;
  const hasImported = items.length > 0;
  const missingCount = summary?.missing_from_master || 0;
  return (
    <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4" onClick={onClose} data-testid="po-import-result">
      <div className="bg-white rounded-2xl shadow-2xl w-full max-w-2xl max-h-[90vh] flex flex-col overflow-hidden" onClick={(e) => e.stopPropagation()}>
        <div className="px-6 py-4 border-b border-slate-100 flex items-start justify-between">
          <div>
            <h3 className="text-lg font-bold text-[#0a2a5e]">Import result</h3>
            <p className="text-xs text-slate-500 mt-1">
              Total rows: <b>{summary.total_rows}</b> · Imported: <b className="text-emerald-700">{summary.imported}</b> · Failed: <b className="text-red-600">{summary.failed}</b>
            </p>
          </div>
          <button onClick={onClose} className="text-slate-400 hover:text-slate-700 p-1" aria-label="Close"><X className="h-5 w-5" /></button>
        </div>

        <div className="flex-1 overflow-y-auto px-6 py-4 space-y-4">
          {errors.length > 0 && (
            <div>
              <div className="flex items-center gap-2 text-red-700 font-semibold mb-2 text-sm"><AlertCircle className="h-4 w-4" /> {errors.length} row{errors.length === 1 ? "" : "s"} failed validation</div>
              <div className="border border-red-200 rounded-lg overflow-hidden">
                <table className="w-full text-xs">
                  <thead className="bg-red-50">
                    <tr>
                      <th className="px-3 py-2 text-left font-semibold text-red-700 w-14">Row</th>
                      <th className="px-3 py-2 text-left font-semibold text-red-700">Item / Description</th>
                      <th className="px-3 py-2 text-left font-semibold text-red-700">Errors</th>
                    </tr>
                  </thead>
                  <tbody>
                    {errors.map((er, i) => (
                      <tr key={i} className="border-t border-red-100">
                        <td className="px-3 py-2 font-mono">{er.row_no}</td>
                        <td className="px-3 py-2">
                          <div className="font-semibold text-slate-800">{er.raw?.item || "—"}</div>
                          <div className="text-slate-500">{er.raw?.description || <span className="italic text-slate-400">(empty)</span>}</div>
                        </td>
                        <td className="px-3 py-2 text-red-600">
                          <ul className="list-disc list-inside space-y-0.5">
                            {er.errors.map((e, j) => <li key={j}>{e}</li>)}
                          </ul>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          )}
          {hasImported && (
            <div>
              <div className="text-emerald-700 font-semibold mb-2 text-sm">{items.length} row{items.length === 1 ? "" : "s"} ready to import</div>
              <div className="border border-slate-200 rounded-lg overflow-hidden max-h-48 overflow-y-auto">
                <table className="w-full text-xs">
                  <thead className="bg-slate-50 sticky top-0">
                    <tr>
                      <th className="px-3 py-2 text-left font-semibold text-slate-500">Item</th>
                      <th className="px-3 py-2 text-left font-semibold text-slate-500">Description</th>
                      <th className="px-3 py-2 text-right font-semibold text-slate-500">Qty</th>
                      <th className="px-3 py-2 text-right font-semibold text-slate-500">Rate</th>
                    </tr>
                  </thead>
                  <tbody>
                    {items.map((it, i) => (
                      <tr key={i} className="border-t border-slate-100">
                        <td className="px-3 py-2 font-semibold">
                          <div className="flex items-center gap-1.5">
                            <span>{it.item || "—"}</span>
                            {it.item && !it.in_master && (
                              <span className="px-1.5 py-0.5 text-[10px] font-bold uppercase rounded bg-amber-100 text-amber-700 border border-amber-200" title="Not in Item Master">NEW</span>
                            )}
                          </div>
                        </td>
                        <td className="px-3 py-2 text-slate-600">{it.description || "—"}</td>
                        <td className="px-3 py-2 text-right">{it.qty} {it.unit}</td>
                        <td className="px-3 py-2 text-right">₹ {inr(it.rate)}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          )}
          {!hasImported && errors.length === 0 && (
            <div className="text-center text-sm text-slate-500 py-6">No rows found in the uploaded file.</div>
          )}
        </div>

        <div className="px-6 py-3 border-t border-slate-100 flex flex-wrap items-center justify-between gap-3 bg-slate-50">
          <label className={`flex items-center gap-2 text-sm select-none ${missingCount > 0 ? "text-slate-700 cursor-pointer" : "text-slate-400"}`} data-testid="po-import-add-to-master-label">
            <input
              type="checkbox"
              checked={addToMaster}
              onChange={(e) => setAddToMaster(e.target.checked)}
              disabled={missingCount === 0 || applyBusy}
              data-testid="po-import-add-to-master"
              className="h-4 w-4 accent-[#0a2a5e] disabled:opacity-40"
            />
            <span>
              Add missing items to Master
              {missingCount > 0 ? (
                <span className="ml-1.5 px-1.5 py-0.5 text-[11px] font-bold rounded bg-amber-100 text-amber-700 border border-amber-200">{missingCount} new</span>
              ) : (
                <span className="ml-1.5 text-xs text-slate-400">(no new items detected)</span>
              )}
            </span>
          </label>
          <div className="flex flex-wrap gap-2">
            <Button type="button" variant="outline" onClick={onClose} disabled={applyBusy} className="rounded-full">Cancel</Button>
            {hasImported && (
              <>
                <Button type="button" variant="outline" onClick={() => onApply("append")} disabled={applyBusy} data-testid="po-import-append" className="rounded-full">
                  {applyBusy ? "…" : "Append to current items"}
                </Button>
                <Button type="button" onClick={() => onApply("replace")} disabled={applyBusy} data-testid="po-import-replace" className="rounded-full bg-[#0a2a5e] hover:bg-[#0a2a5e]/90 text-white">
                  {applyBusy ? "…" : "Replace current items"}
                </Button>
              </>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
